import React from 'react'
import {
    Box,
    CircularProgress,
    LinearProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import BuildIcon from '@material-ui/icons/Build'
import CheckIcon from '@material-ui/icons/Check'

import { Method } from '../types'
import { GEN_STEPS } from '../websiteData'
import { useWebsiteStyles } from '../useWebsiteStyles'

const useStyles = makeStyles((theme: Theme) => ({
    wrapper: {
        maxWidth: 576,
        margin: '0 auto',
    },
    cardInner: {
        padding: theme.spacing(4),
        textAlign: 'center',
    },
    stepRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.75rem',
    },
    stepDone: {
        color: theme.palette.text.primary,
    },
    stepActive: {
        color: theme.palette.primary.main,
    },
    stepPending: {
        color: theme.palette.text.secondary,
        opacity: 0.5,
    },
    stepCircle: {
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: `1px solid currentColor`,
        flexShrink: 0,
    },
    stepsList: {
        marginTop: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        textAlign: 'left',
    },
}))

interface CreatingViewProps {
    method: Method
    step: number
}

const METHOD_LABELS: Record<Method, string> = {
    ai: 'Generating your site with AI',
    clone: 'Cloning the style',
    template: 'Applying template',
}

export const CreatingView: React.FC<CreatingViewProps> = ({ method, step }) => {
    const classes = useStyles()
    const sharedClasses = useWebsiteStyles()
    const pct = Math.min(100, Math.round((step / GEN_STEPS.length) * 100))

    return (
        <Box className={classes.wrapper}>
            <Box className={`${sharedClasses.card} ${classes.cardInner}`}>
                <BuildIcon
                    color="primary"
                    style={{ fontSize: 32, animation: 'pulse 2s infinite' }}
                />
                <Typography
                    variant="h6"
                    style={{ fontWeight: 600, marginTop: 12 }}
                >
                    {METHOD_LABELS[method]}
                </Typography>
                <Box mt={2.5}>
                    <LinearProgress
                        variant="determinate"
                        value={pct}
                        style={{ height: 8, borderRadius: 4 }}
                    />
                </Box>
                <Box className={classes.stepsList}>
                    {GEN_STEPS.map((s, i) => (
                        <Box
                            key={s}
                            className={`${classes.stepRow} ${
                                i < step
                                    ? classes.stepDone
                                    : i === step
                                    ? classes.stepActive
                                    : classes.stepPending
                            }`}
                        >
                            {i < step ? (
                                <CheckIcon style={{ fontSize: 14 }} />
                            ) : i === step ? (
                                <CircularProgress size={14} />
                            ) : (
                                <span className={classes.stepCircle} />
                            )}
                            {s}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}
